/**
 * In-memory (session) cache for body-weight API data.
 * Survives SPA navigation; cleared on full page reload or explicit invalidate
 * after local create / update / delete.
 */

import { createSignal } from "solid-js";
import { api } from "../api/client";
import type { BodyWeightLog, BodyWeightSummary } from "../api/types";
import { todayISO } from "./repeatWorkout";

export type WeightPeriod = "30d" | "90d" | "all";

const summaryCache = new Map<WeightPeriod, BodyWeightSummary>();
const logsCache = new Map<WeightPeriod, BodyWeightLog[]>();

/** Bumps when cache is cleared so subscribers can react if needed. */
const [cacheEpoch, setCacheEpoch] = createSignal(0);

export function bodyWeightCacheEpoch() {
  return cacheEpoch();
}

export function invalidateBodyWeightCache() {
  summaryCache.clear();
  logsCache.clear();
  setCacheEpoch((n) => n + 1);
}

function rangeForPeriod(p: WeightPeriod): { from?: string; to: string } {
  const to = todayISO();
  if (p === "30d") {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return { from: todayISO(d), to };
  }
  if (p === "90d") {
    const d = new Date();
    d.setDate(d.getDate() - 90);
    return { from: todayISO(d), to };
  }
  return { to };
}

export async function getBodyWeightSummary(
  period: WeightPeriod,
): Promise<BodyWeightSummary> {
  const hit = summaryCache.get(period);
  if (hit) return hit;
  const data = await api.bodyWeightSummary(period);
  summaryCache.set(period, data);
  return data;
}

export async function getBodyWeightLogs(
  period: WeightPeriod,
): Promise<BodyWeightLog[]> {
  const hit = logsCache.get(period);
  if (hit) return hit;
  const { from, to } = rangeForPeriod(period);
  const list = await api.listBodyWeight({ from, to, limit: 500 });
  logsCache.set(period, list);
  return list;
}

/** Load logs + summary for a period (cache-first, parallel on miss). */
export async function loadWeightPeriod(period: WeightPeriod): Promise<{
  logs: BodyWeightLog[];
  summary: BodyWeightSummary;
  fromCache: boolean;
}> {
  const hadLogs = logsCache.has(period);
  const hadSum = summaryCache.has(period);
  const [logs, summary] = await Promise.all([
    getBodyWeightLogs(period),
    getBodyWeightSummary(period),
  ]);
  return { logs, summary, fromCache: hadLogs && hadSum };
}
